(function () {
  'use strict';

  var INSTANCE_PROP = '__axiomLenisController';
  var RAF_PROP = '__axiomLenisRaf';
  var lastResizeWidth = window.innerWidth;
  var lastResizeHeight = window.innerHeight;
  var scrollLockObserver = null;
  var vendorLoadPromise = null;
  var reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var primaryTouchQuery = window.matchMedia
    ? window.matchMedia('(hover: none) and (pointer: coarse)')
    : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function usesNativeTouchScrolling() {
    return Boolean(primaryTouchQuery && primaryTouchQuery.matches);
  }

  function canUseLenis() {
    return isEnabled() && !prefersReducedMotion() && !usesNativeTouchScrolling();
  }

  function loadLenisVendor() {
    if (typeof window.Lenis === 'function') return Promise.resolve();
    if (vendorLoadPromise) return vendorLoadPromise;

    var source = document.body && document.body.dataset.lenisVendorScript;
    if (!source) return Promise.resolve();

    vendorLoadPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = source;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return vendorLoadPromise;
  }

  function isEnabled() {
    return document.body && document.body.getAttribute('data-lenis-enabled') === 'true';
  }

  function dispatchThemeResize() {
    document.dispatchEvent(new CustomEvent('theme:resize', {
      detail: {
        width: window.innerWidth,
        height: window.innerHeight,
        source: 'lenis'
      }
    }));
  }

  function isPageScrollLocked() {
    return document.documentElement.classList.contains('is-scroll-locked')
      || (document.body && document.body.style.overflow === 'hidden');
  }

  function isNativeScrollableOverlay(node) {
    return Boolean(
      node
      && node.nodeType === 1
      && node.matches
      && node.matches('top-level-modal')
    );
  }

  function syncPageScrollLock(controller) {
    if (!controller || !controller.lenis) return;

    if (isPageScrollLocked()) {
      controller.lenis.stop();
      return;
    }

    controller.lenis.start();
  }

  function observePageScrollLock(controller) {
    if (!document.body || typeof MutationObserver !== 'function') return;

    if (scrollLockObserver) scrollLockObserver.disconnect();
    scrollLockObserver = new MutationObserver(function () {
      syncPageScrollLock(controller);
    });
    scrollLockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    scrollLockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });
    syncPageScrollLock(controller);
  }

  function resolveNativeScrollTop(target, offset) {
    if (typeof target === 'number') return target + offset;

    var element = target;
    if (typeof target === 'string') {
      if (target === 'top' || target === 'start' || target === '#') return offset;
      if (target === 'bottom' || target === 'end') {
        return document.documentElement.scrollHeight - window.innerHeight + offset;
      }
      element = document.querySelector(target);
    }

    if (!element || element.nodeType !== 1) return null;
    return element.getBoundingClientRect().top + window.scrollY + offset;
  }

  function installPublicApi() {
    window.Axiom = window.Axiom || {};
    window.Axiom.scrollTo = function (target, options) {
      var settings = options || {};
      var controller = window[INSTANCE_PROP];

      if (controller && controller.lenis) {
        controller.lenis.scrollTo(target, {
          immediate: Boolean(settings.immediate),
          offset: settings.offset || 0
        });
        return;
      }

      var scrollTop = resolveNativeScrollTop(target, settings.offset || 0);
      if (scrollTop === null) return;
      window.scrollTo({
        top: scrollTop,
        behavior: settings.immediate || prefersReducedMotion() ? 'auto' : 'smooth'
      });
    };
    window.Axiom.stopSmoothScroll = function () {
      var controller = window[INSTANCE_PROP];
      if (controller && controller.lenis) controller.lenis.stop();
    };
    window.Axiom.startSmoothScroll = function () {
      var controller = window[INSTANCE_PROP];
      if (controller && controller.lenis && !isPageScrollLocked()) controller.lenis.start();
    };
  }

  function createLenis() {
    if (
      !isEnabled()
      || prefersReducedMotion()
      || usesNativeTouchScrolling()
      || typeof window.Lenis !== 'function'
    ) return null;
    if (window[INSTANCE_PROP]) return window[INSTANCE_PROP];

    var lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
      prevent: isNativeScrollableOverlay
    });

    var controller = {
      lenis: lenis,
      rafId: 0,
      destroy: function () {
        if (this.rafId) window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
        if (this.wake) window.removeEventListener('wheel', this.wake, { capture: true });
        if (scrollLockObserver) {
          scrollLockObserver.disconnect();
          scrollLockObserver = null;
        }
        lenis.destroy();
        if (window.lenis === lenis) window.lenis = null;
        if (window.Axiom && window.Axiom.lenis === lenis) window.Axiom.lenis = null;
        window[INSTANCE_PROP] = null;
        window[RAF_PROP] = null;
      }
    };

    // The loop runs only while Lenis has a scroll to animate: a frame loop that
    // never ends makes an idle page render every frame, which low-end machines
    // feel as jank everywhere else. Wheel input and scrollTo calls wake it; it
    // sleeps once the smooth scroll has settled.
    var lastWake = 0;

    function raf(time) {
      lenis.raf(time);
      if (lenis.isScrolling !== 'smooth' && performance.now() - lastWake > 250) {
        controller.rafId = 0;
        window[RAF_PROP] = 0;
        return;
      }
      controller.rafId = window.requestAnimationFrame(raf);
      window[RAF_PROP] = controller.rafId;
    }

    function wake() {
      lastWake = performance.now();
      if (controller.rafId) return;
      // Lenis times each step from its previous frame; after a sleep that gap
      // would make the first step jump straight to the target.
      lenis.time = 0;
      controller.rafId = window.requestAnimationFrame(raf);
      window[RAF_PROP] = controller.rafId;
    }

    controller.wake = wake;
    window.addEventListener('wheel', wake, { passive: true, capture: true });

    ['scrollTo', 'start', 'resize'].forEach(function (method) {
      var original = lenis[method];
      if (typeof original !== 'function') return;
      lenis[method] = function () {
        var result = original.apply(lenis, arguments);
        wake();
        return result;
      };
    });

    wake();
    window[INSTANCE_PROP] = controller;
    window.lenis = lenis;
    window.Axiom = window.Axiom || {};
    window.Axiom.lenis = lenis;

    observePageScrollLock(controller);
    return controller;
  }

  // Lenis reads the page scroll and size when it is created. Created from a boot script, that read
  // forces a whole-page style and layout pass in the middle of the other scripts' DOM writes; in
  // a frame callback it reuses the frame's own pass.
  var createFrame = 0;
  function createLenisNextFrame() {
    if (createFrame) return;
    createFrame = window.requestAnimationFrame(function () {
      createFrame = 0;
      if (canUseLenis()) createLenis();
    });
  }

  function syncLenisEligibility() {
    var controller = window[INSTANCE_PROP];
    var shouldRun = canUseLenis();

    if (!shouldRun) {
      if (controller) controller.destroy();
      return;
    }

    if (controller) return;
    if (typeof window.Lenis === 'function') {
      createLenisNextFrame();
      return;
    }

    loadLenisVendor().then(function () {
      if (canUseLenis()) createLenisNextFrame();
    }).catch(function () {
      vendorLoadPromise = null;
    });
  }

  function handleResize() {
    if (window.innerWidth === lastResizeWidth && window.innerHeight === lastResizeHeight) return;
    lastResizeWidth = window.innerWidth;
    lastResizeHeight = window.innerHeight;

    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      controller.lenis.resize();
    }

    dispatchThemeResize();
  }

  function initLenis() {
    installPublicApi();
    syncLenisEligibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLenis, { once: true });
  } else {
    initLenis();
  }

  window.addEventListener('resize', handleResize, { passive: true });
  if (reducedMotionQuery) reducedMotionQuery.addEventListener('change', syncLenisEligibility);
  if (primaryTouchQuery) primaryTouchQuery.addEventListener('change', syncLenisEligibility);
  window.addEventListener('pageshow', function () {
    syncLenisEligibility();
    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      controller.lenis.resize();
    }
    syncPageScrollLock(controller);
  });

  document.addEventListener('shopify:section:load', function () {
    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      window.requestAnimationFrame(function () {
        controller.lenis.resize();
        dispatchThemeResize();
      });
    }
  });
})();
